import i18next from 'i18next'
import {
  ContractChangeFragment,
  FullMeetingFragment,
  MeetingInput,
  MeetingStepStatus,
  MeetingStepViewId,
  MeetingType,
  ObjectiveStatus,
  ObjectiveDto,
  SalaryOfYearFragment,
  ScoreGoalDto,
  TargetBonusOfYearFragment,
  Currency,
  CurrencyConversionDto,
} from '../../../../types'
import { AppraisalContextType } from '../../AppraisalTypes'
import { filterObjectivesByReferenceYear, filterObjectiveDtoListByStatus } from '../../okr/util'
import { calculateBonusWithSalary, ContractWithValidity, FlexibleBonusOverride, ProrataPartial } from './targetBonusCalculation-util'
import moment from 'moment-timezone'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'
import { isDefined } from '../../../../types.helper'

export const calculatePastTargetsData = (
  employeeTimezone: string,
  isBonusValidationEnabled: boolean,
  targetBonusOfYear: TargetBonusOfYearFragment[],
  salaryOfYear: SalaryOfYearFragment[],
  currentSalary: number,
  currentBonus: number,
  contractChanges: ContractChangeFragment[] | undefined = [],
  objectives: ObjectiveDto[],
  currencyConversionData: CurrencyConversionDto[],
  currentContractCurrency: Currency,
  referenceYear: number,
  startDate: string,
  endDate?: string,
  contractsInYear: ContractWithValidity[] = [],
  flexibleBonusOverride?: FlexibleBonusOverride,
  flexibleBonusUnknownCurrency?: Currency,
): {
  openTargetBonus: number
  targetBonusAmount: number
  targetBonusToBePaid: number
  openTargetBonusIsNegative: boolean
  unknownCurrenciesForConversion: Currency[]
  outdatedTargets: ObjectiveDto[]
} => {
  // Select all monetary targets that are in selected year and that don't have
  // one of following statuses: Proposal, ProposalFromManager, DismissedPending, Dismissed
  const monetaryTargetsOfTheYear: ObjectiveDto[] = objectives.filter(
    (o) =>
      o.targetAmount !== null &&
      [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved, ObjectiveStatus.Closed].includes(o.status) &&
      o.appraisalSeason.year === referenceYear,
  )

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      [
        ...monetaryTargetsOfTheYear
          .filter((obj): boolean => {
            if (obj.currency! !== currentContractCurrency) {
              const conversionRate: number | undefined = retrieveCurrencyConversionRate(
                obj.currency!,
                currentContractCurrency,
                currencyConversionData,
              )
              if (!conversionRate) {
                return true
              }
            }
            return false
          })
          .map((obj) => obj.currency!),
        ...(flexibleBonusUnknownCurrency ? [flexibleBonusUnknownCurrency] : []),
      ].filter((currency): currency is Currency => !!currency),
    ),
  ]

  // Calculate already paid out targets -> Closed targets with achieved amount from: 1. last year in case of appraisal or 2. this year in case of appraisal update
  const alreadyPaidOutAmount = monetaryTargetsOfTheYear.reduce((acc, objective) => {
    if (
      objective.achievedAmount !== null &&
      objective.achievedAmount !== undefined &&
      objective.targetAmount !== null &&
      objective.targetAmount !== undefined &&
      objective.status === ObjectiveStatus.Closed
    ) {
      if (objective.currency === currentContractCurrency) {
        acc += objective.targetAmount
      } else {
        const conversionRate: number | undefined = retrieveCurrencyConversionRate(
          objective.currency!,
          currentContractCurrency,
          currencyConversionData,
        )
        if (!!conversionRate) {
          acc += Number((objective.targetAmount * conversionRate).toFixed(2))
        } else {
          acc += objective.targetAmount
        }
      }
    }
    return acc
  }, 0)

  const targetBonusCalc = calculateBonusWithSalary(
    employeeTimezone,
    isBonusValidationEnabled,
    referenceYear,
    contractChanges,
    targetBonusOfYear,
    salaryOfYear,
    currentSalary,
    currentBonus,
    currencyConversionData,
    currentContractCurrency,
    startDate,
    endDate,
    contractsInYear,
    flexibleBonusOverride,
  )
  const targetBonusOfTheYear = targetBonusCalc.flexibleBonusAmount ?? targetBonusCalc.amount

  const targetBonusAmount = Number((targetBonusOfTheYear - alreadyPaidOutAmount).toFixed(2))

  // Calculate target bonus amount for each monetary target that is scheduled
  const alreadyScheduledMonetaryTargetsAmount = monetaryTargetsOfTheYear.reduce((acc, o) => {
    if (!!o.targetAmount && o.status !== ObjectiveStatus.Closed && o.closedDate === null) {
      if (o.currency !== currentContractCurrency) {
        const conversionRate: number | undefined = retrieveCurrencyConversionRate(
          o.currency!,
          currentContractCurrency,
          currencyConversionData,
        )
        if (!!conversionRate) {
          acc += o.targetAmount * conversionRate
        } else {
          acc += o.targetAmount
        }
      } else {
        acc += o.targetAmount
      }
    }
    return acc
  }, 0)

  // Calculate how much open target bonus is left
  const openTargetBonusRaw = targetBonusAmount - alreadyScheduledMonetaryTargetsAmount
  const openTargetBonus = Number(openTargetBonusRaw.toFixed(2))

  // Calculate how much of target bonus is actually paid out -> adding achievedAmount not targetAmount
  const targetBonusToBePaid = Number(
    monetaryTargetsOfTheYear
      .reduce((acc, o) => {
        if (!!o.achievedAmount && (o.status === ObjectiveStatus.Approved || o.status === ObjectiveStatus.PastApproved)) {
          if (o.currency === currentContractCurrency) {
            acc += o.achievedAmount
          } else {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(
              o.currency!,
              currentContractCurrency,
              currencyConversionData,
            )
            if (!!conversionRate) {
              acc += o.achievedAmount * conversionRate
            } else {
              acc += o.achievedAmount
            }
          }
        }
        return acc
      }, 0)
      .toFixed(2),
  )

  const openTargetBonusIsNegative: boolean = openTargetBonus < 0

  const outdatedTargets = objectives.filter(
    (objective) =>
      objective.appraisalSeason.year < referenceYear &&
      [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved].some((status) => objective.status === status),
  )

  return {
    openTargetBonus,
    targetBonusAmount,
    targetBonusToBePaid,
    openTargetBonusIsNegative,
    unknownCurrenciesForConversion,
    outdatedTargets,
  }
}

export const calculateNewTargetsData = (
  allowedNewTargets: boolean,
  employeeTimezone: string,
  isBonusValidationEnabled: boolean,
  flexibleBonusOverride: FlexibleBonusOverride | undefined,
  objectives: ObjectiveDto[],
  targetBonusOfYear: TargetBonusOfYearFragment[],
  salaryOfYear: SalaryOfYearFragment[],
  currentContractCurrency: Currency,
  currentSalary: number,
  currentBonus: number,
  contractChanges: ContractChangeFragment[] | undefined = [],
  isAppraisalUpdate: boolean,
  referenceYear: number,
  startDate: string,
  currencyConversionData: CurrencyConversionDto[],
  endDate?: string,
  contractsInYear: ContractWithValidity[] = [],
  flexibleBonusUnknownCurrency?: Currency,
): {
  annualBonus: number
  prorataAmount: number
  prorataPartials: ProrataPartial[]
  flexibleBonusAmount?: number
  targetBonusOfTheYear: number
  targetBonusRemaining: number
  alreadyPlanned: number
  cannotProceed: boolean
  hasBonusOrWillHaveBonus: boolean
  isMonetaryTargetMissing: boolean
  tooMuchTargetBonusProvided: boolean
  alreadyPaidObjectivesAmount: number
  alreadyPlannedAndPaid: number
  atLeastOneMonetaryTargetWithWrongCurrency: boolean
  unknownCurrenciesForConversion: Currency[]
} => {
  const unknownCurrenciesForConversion: Currency[] = [...(flexibleBonusUnknownCurrency ? [flexibleBonusUnknownCurrency] : [])]

  const addUnknownCurrency = (currency: Currency): void => {
    if (!unknownCurrenciesForConversion.includes(currency)) {
      unknownCurrenciesForConversion.push(currency)
    }
  }

  let currentObjectives = isAppraisalUpdate
    ? filterObjectiveDtoListByStatus(objectives, [
        ObjectiveStatus.Proposal,
        ObjectiveStatus.ProposalFromManager,
        ObjectiveStatus.Approved,
        ObjectiveStatus.Closed,
      ])
    : filterObjectiveDtoListByStatus(objectives, [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved])
  currentObjectives = filterObjectivesByReferenceYear(currentObjectives, referenceYear)

  currentObjectives.forEach((target) => {
    // currentObjectives can also contain dev targets
    if (isDefined(target.targetAmount) && target.currency! !== currentContractCurrency) {
      const conversionRate: number | undefined = retrieveCurrencyConversionRate(
        target.currency!,
        currentContractCurrency,
        currencyConversionData,
      )
      if (!conversionRate) {
        addUnknownCurrency(target.currency!)
      }
    }
  })

  const alreadyPaidObjectivesAmount = Number(
    currentObjectives
      .reduce((acc, objective) => {
        if (
          objective.achievedAmount !== null &&
          objective.achievedAmount !== undefined &&
          objective.targetAmount !== null &&
          objective.targetAmount !== undefined &&
          objective.status === ObjectiveStatus.Closed
        ) {
          if (objective.currency === currentContractCurrency) {
            acc += objective.targetAmount
          } else {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(
              objective.currency!,
              currentContractCurrency,
              currencyConversionData,
            )
            if (conversionRate !== undefined) {
              acc += Number((objective.targetAmount * conversionRate).toFixed(2))
            } else {
              addUnknownCurrency(objective.currency!)
            }
          }
        }
        return acc
      }, 0)
      .toFixed(2),
  )

  const sumOfCurrentTargetBonus = currentObjectives.reduce((sum, obj) => {
    if (isDefined(obj.targetAmount) && ![ObjectiveStatus.Closed].includes(obj.status)) {
      if (obj.currency !== currentContractCurrency) {
        const currencyConversionRate: number | undefined = retrieveCurrencyConversionRate(
          obj.currency!,
          currentContractCurrency,
          currencyConversionData,
        )
        if (currencyConversionRate !== undefined) {
          return sum + Number((obj.targetAmount! * currencyConversionRate).toFixed(2))
        }
        return sum
      }
      return sum + obj.targetAmount!
    }
    return sum
  }, 0)

  const alreadyPlanned = Number(sumOfCurrentTargetBonus.toFixed(2))

  const alreadyPlannedAndPaid = Number((alreadyPaidObjectivesAmount + alreadyPlanned).toFixed(2))

  const prorataCalc = calculateBonusWithSalary(
    employeeTimezone,
    isBonusValidationEnabled,
    referenceYear,
    contractChanges,
    targetBonusOfYear,
    salaryOfYear,
    currentSalary,
    currentBonus,
    currencyConversionData,
    currentContractCurrency!,
    startDate,
    endDate,
    contractsInYear,
    flexibleBonusOverride,
  )

  const prorataAmount = prorataCalc.amount

  const prorataPartials = prorataCalc.partials

  const flexibleBonusAmount = prorataCalc.flexibleBonusAmount

  const targetBonusByChanges = contractChanges.find((cc) => {
    const changes = JSON.parse(cc.changes)
    const targetBonus = changes.bonus
    return targetBonus !== undefined
  })
  const yearOfChanges =
    targetBonusByChanges && targetBonusByChanges.validFrom ? moment.utc(targetBonusByChanges.validFrom).year() : undefined

  const annualBonus =
    targetBonusByChanges && yearOfChanges && [referenceYear, referenceYear - 1].includes(yearOfChanges)
      ? Number(JSON.parse(targetBonusByChanges.changes).bonus.toFixed(2))
      : Number(currentBonus.toFixed(2))

  const targetBonusOfTheYear = flexibleBonusAmount ?? prorataAmount

  const targetBonusRemaining = Number((targetBonusOfTheYear - alreadyPlannedAndPaid).toFixed(2))

  const targetBonusIsNotZero: boolean =
    allowedNewTargets && targetBonusOfYear.reduce((sum: number, bonus: TargetBonusOfYearFragment) => sum + bonus.amount, 0) > 0

  const hasBonusOrWillHaveBonus: boolean = targetBonusIsNotZero || hasBonusChanges(contractChanges, referenceYear)

  const isMonetaryTargetMissing = allowedNewTargets && targetBonusRemaining > 0

  const tooMuchTargetBonusProvided = allowedNewTargets && targetBonusRemaining < 0

  const atLeastOneMonetaryTargetWithWrongCurrency = currentObjectives.some(
    (obj) =>
      isDefined(obj.targetAmount) &&
      [ObjectiveStatus.Approved, ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager].includes(obj.status) &&
      obj.currency !== currentContractCurrency,
  )

  const cannotProceed =
    isMonetaryTargetMissing ||
    tooMuchTargetBonusProvided ||
    atLeastOneMonetaryTargetWithWrongCurrency ||
    unknownCurrenciesForConversion.length > 0

  return {
    annualBonus,
    prorataAmount,
    prorataPartials,
    flexibleBonusAmount,
    targetBonusOfTheYear,
    targetBonusRemaining,
    alreadyPlanned,
    cannotProceed,
    hasBonusOrWillHaveBonus,
    isMonetaryTargetMissing,
    tooMuchTargetBonusProvided,
    alreadyPaidObjectivesAmount,
    alreadyPlannedAndPaid,
    atLeastOneMonetaryTargetWithWrongCurrency,
    unknownCurrenciesForConversion,
  }
}

const createInput = (meeting: FullMeetingFragment): MeetingInput =>
  ({
    id: meeting.id,
    date: meeting.date,
    displayDate: meeting.displayDate,
    employeePrepared: meeting.employeePrepared,
    managerPrepared: meeting.managerPrepared,
    appraisal: {
      targetCommentsShared: meeting.appraisal ? meeting.appraisal.targetCommentsShared : false,
      evaluationShared: meeting.appraisal ? meeting.appraisal.evaluationShared : false,
      selfReflectionShared: meeting.appraisal ? meeting.appraisal.selfReflectionShared : false,
      employeeGeneralFeedback: meeting.appraisal ? meeting.appraisal.employeeGeneralFeedback : null,
      reviewerGeneralFeedback: meeting.appraisal ? meeting.appraisal.reviewerGeneralFeedback : null,
      meetingGeneralFeedback: meeting.appraisal ? meeting.appraisal.meetingGeneralFeedback : null,
    },
    other: meeting.other,
    status: meeting.status,
  } as MeetingInput)

const hasBonusChanges = (changes: ContractChangeFragment[], referenceYear: number): boolean => {
  return (
    changes
      .map((c: ContractChangeFragment) => {
        return {
          amount: (JSON.parse(c.changes).bonus as number) || 0,
          fromDate: moment.utc(c.validFrom),
          toDate: null,
        }
      })
      .filter((c) => c.amount > 0 && [referenceYear, referenceYear - 1].includes(c.fromDate.year())).length > 0
  )
}

const getEvaluationStepMeetingStatus = (scoreGoals: ScoreGoalDto[], type: AppraisalContextType): MeetingStepStatus => {
  const allScoresFinished = scoreGoals.every((goal) => {
    if (type === AppraisalContextType.Employee) {
      return goal.employeeEvaluation
    } else if (type === AppraisalContextType.Manager) {
      return goal.reviewerEvaluation
    } else {
      return goal.resultEvaluation
    }
  })
  const scoresNotStarted = scoreGoals.every((goal) => {
    if (type === AppraisalContextType.Employee) {
      return !goal.employeeEvaluation
    } else if (type === AppraisalContextType.Manager) {
      return !goal.reviewerEvaluation
    } else {
      return !goal.resultEvaluation
    }
  })

  if (allScoresFinished) {
    return MeetingStepStatus.Done
  } else if (scoresNotStarted) {
    return MeetingStepStatus.Visited
  } else {
    return MeetingStepStatus.Incomplete
  }
}

const generateStepName = (stepViewId: MeetingStepViewId, meetingType: MeetingType): string => {
  const isAppraisalUpdate = meetingType === MeetingType.AppraisalUpdate
  const isNewTargetsStep = stepViewId === MeetingStepViewId.NewTargets

  if (isAppraisalUpdate && isNewTargetsStep) {
    return i18next.t('appraisalMeeting.meetingStepNamesByViewId.DevelopmentAndTargets')
  }
  return i18next.t(`appraisalMeeting.meetingStepNamesByViewId.${stepViewId}`)
}

const getIncompleteStepReason = (status: MeetingStepStatus): string => {
  if (status === MeetingStepStatus.Visited || status === MeetingStepStatus.Todo) {
    return i18next.t('appraisalMeeting.stepNotes.infoCard.notStarted')
  } else if (status === MeetingStepStatus.Incomplete) {
    return i18next.t('appraisalMeeting.stepNotes.infoCard.notCompleted')
  } else {
    return ''
  }
}

export { createInput, hasBonusChanges, getEvaluationStepMeetingStatus, generateStepName, getIncompleteStepReason }
